// utils
import { getEnabledFeatures, hasFeature as tierHasFeature, DEFAULT_TIER } from '../config/erpFeatureFlags';

// ----------------------------------------------------------------------

/**
 * ERP role groups (mirrors backend src/erp/erpRoles.ts). A user may hold
 * several of these via Cognito group membership.
 */
export const ERP_ROLE_GROUPS = {
  accounting: 'erp_accounting',
  sales: 'erp_sales',
  warehouse: 'erp_warehouse',
  production: 'erp_production',
  packaging: 'erp_packaging',
};

export const ERP_ROLES = Object.keys(ERP_ROLE_GROUPS);

/**
 * Whether the site has the ERP module turned on at all.
 * Set via site.erpEnabled on the site record in the Frigid table.
 */
export function isErpEnabled(site) {
  return Boolean(site && site.erpEnabled === true);
}

/**
 * Whether this is a STANDALONE ERP site — ERP only, no Frigid hardware.
 * Existing hardware sites never set hardwareEnabled, so undefined !== false
 * and they are unaffected. Standalone sites explicitly set it false.
 */
export function isStandaloneErp(site) {
  return isErpEnabled(site) && site && site.hardwareEnabled === false;
}

function isSiteMember(user, siteId) {
  if (!user || !siteId) return false;
  if (user.activeSite === siteId) return true;
  const sites = user.sites || [];
  return sites.some((s) => s && (s.id === siteId || s === siteId));
}

/** Full ERP access: system admin, account admin, or the legacy `erp` group. */
export function isFullErpUser(user) {
  if (!user) return false;
  if (user.role === 'admin' || user.role === 'account') return true;
  const groups = user.groups || [];
  return Array.isArray(groups) && groups.includes('erp');
}

/** Role keys (accounting/sales/warehouse/production/packaging) the user holds. */
export function getErpRoles(user) {
  const groups = (user && user.groups) || [];
  if (!Array.isArray(groups)) return [];
  return ERP_ROLES.filter((key) => groups.includes(ERP_ROLE_GROUPS[key]));
}

/** Whether the user holds at least one of the requested ERP roles (or full access). */
export function hasErpRole(user, roles) {
  if (!user) return false;
  if (isFullErpUser(user)) return true;
  const wanted = Array.isArray(roles) ? roles : [roles];
  if (wanted.length === 0) return true;
  const groups = user.groups || [];
  if (!Array.isArray(groups)) return false;
  return wanted.some((key) => {
    const group = ERP_ROLE_GROUPS[key];
    return group ? groups.includes(group) : false;
  });
}

/**
 * Whether the current user is allowed to use ERP.
 * - frigid admin/account roles, the legacy `erp` group, or any ERP role group.
 * Standalone ERP sites additionally require site membership.
 */
export function isErpUser(user) {
  if (!user) return false;
  if (isFullErpUser(user)) return true;
  const groups = user.groups || [];
  if (!Array.isArray(groups)) return false;
  return ERP_ROLES.some((key) => groups.includes(ERP_ROLE_GROUPS[key]));
}

/** Both site-level and user-level access must pass */
export function canAccessErp(site, user) {
  if (!isErpEnabled(site)) return false;
  if (!isErpUser(user)) return false;
  if (isStandaloneErp(site)) {
    return isSiteMember(user, site.id);
  }
  return true;
}

/**
 * Combined module access: ERP enabled + site tier feature + (optional) role.
 * Pass `roles` to restrict a module/action to specific ERP roles.
 */
export function canAccessErpModule(site, user, feature, roles) {
  if (!canAccessErp(site, user)) return false;
  if (feature && !hasErpFeature(site, feature)) return false;
  if (roles && roles.length) {
    return hasErpRole(user, roles);
  }
  return true;
}

/** Features available for the site's tier */
export function getErpFeatures(site) {
  return getEnabledFeatures((site && site.erpTier) || DEFAULT_TIER);
}

/** Check a single feature against the site's tier */
export function hasErpFeature(site, feature) {
  return tierHasFeature((site && site.erpTier) || DEFAULT_TIER, feature);
}